import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, MapPin, ExternalLink, Check, AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useDuplicates, useDuplicateSummary, useDismissDuplicate } from '@/api/duplicates';
import type { DuplicateCandidate, DuplicateConfidence } from '@/types/api';
import { DuplicateGroupsTab } from '@/components/duplicates/DuplicateGroupsTab';

const confidenceConfig: Record<
  DuplicateConfidence,
  { label: string; color: string; description: string }
> = {
  high: {
    label: 'High',
    color: 'bg-red-100 text-red-800 border-red-200',
    description: 'Within 50 meters - very likely the same property',
  },
  medium: {
    label: 'Medium',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    description: 'Within 200 meters - possibly the same property',
  },
  low: {
    label: 'Low',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    description: 'Within 500 meters - worth reviewing',
  },
};

function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${String(Math.round(meters))}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
}

interface DuplicateCardProps {
  duplicate: DuplicateCandidate;
  onDismiss: () => void;
  isDismissing: boolean;
}

function DuplicateCard({ duplicate, onDismiss, isDismissing }: DuplicateCardProps) {
  const config = confidenceConfig[duplicate.confidence];

  return (
    <Card data-testid={`duplicate-card-${duplicate.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Copy className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Potential Duplicate</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={cn('text-xs', config.color)}>
              {config.label} confidence
            </Badge>
            <Badge variant="secondary" className="text-xs">
              <MapPin className="mr-1 h-3 w-3" />
              {formatDistance(duplicate.distanceMeters)}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2">
          {/* Location A */}
          <div className="rounded-lg border bg-muted/30 p-3">
            <div className="mb-2 flex items-center justify-between">
              <Badge variant="outline" className="text-xs">
                {duplicate.locationA.partnerAccount.partnerType}
              </Badge>
              <Link
                to={`/synced-locations/${duplicate.locationA.id}`}
                className="text-xs text-primary hover:underline"
              >
                <ExternalLink className="mr-1 inline h-3 w-3" />
                View
              </Link>
            </div>
            <h4 className="font-medium">{duplicate.locationA.name}</h4>
            <p className="text-sm text-muted-foreground">
              {duplicate.locationA.addressFull ?? duplicate.locationA.addressCity ?? 'No address'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Account: {duplicate.locationA.partnerAccount.name}
            </p>
          </div>

          {/* Location B */}
          <div className="rounded-lg border bg-muted/30 p-3">
            <div className="mb-2 flex items-center justify-between">
              <Badge variant="outline" className="text-xs">
                {duplicate.locationB.partnerAccount.partnerType}
              </Badge>
              <Link
                to={`/synced-locations/${duplicate.locationB.id}`}
                className="text-xs text-primary hover:underline"
              >
                <ExternalLink className="mr-1 inline h-3 w-3" />
                View
              </Link>
            </div>
            <h4 className="font-medium">{duplicate.locationB.name}</h4>
            <p className="text-sm text-muted-foreground">
              {duplicate.locationB.addressFull ?? duplicate.locationB.addressCity ?? 'No address'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Account: {duplicate.locationB.partnerAccount.name}
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{config.description}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={onDismiss}
            disabled={isDismissing}
            data-testid={`dismiss-duplicate-${duplicate.id}`}
          >
            <Check className="mr-1 h-3 w-3" />
            {isDismissing ? 'Dismissing...' : 'Not a duplicate'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function DuplicatesPage() {
  const [confidenceFilter, setConfidenceFilter] = useState<DuplicateConfidence | 'all'>('all');
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [confirmDismiss, setConfirmDismiss] = useState<DuplicateCandidate | null>(null);

  const { data: summary } = useDuplicateSummary();
  const { data: duplicatesData, isLoading } = useDuplicates({
    confidence: confidenceFilter === 'all' ? undefined : confidenceFilter,
    limit: 50,
  });

  const dismissMutation = useDismissDuplicate();

  const handleDismiss = async (duplicate: DuplicateCandidate) => {
    setDismissingId(duplicate.id);
    try {
      await dismissMutation.mutateAsync({ duplicateId: duplicate.id });
    } finally {
      setDismissingId(null);
      setConfirmDismiss(null);
    }
  };

  return (
    <div className="container mx-auto py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Duplicate Detection</h1>
          <p className="text-muted-foreground">
            Review and resolve potential duplicate locations across partner accounts
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="mb-6 grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold">{summary.total}</div>
                <p className="text-sm text-muted-foreground">Total Duplicates</p>
              </div>
            </CardContent>
          </Card>
          <Card className={summary.byConfidence.high > 0 ? 'border-red-200' : ''}>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1">
                  {summary.byConfidence.high > 0 && (
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                  )}
                  <span className="text-3xl font-bold text-red-600">
                    {summary.byConfidence.high}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">High Confidence</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600">
                  {summary.byConfidence.medium}
                </div>
                <p className="text-sm text-muted-foreground">Medium Confidence</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600">{summary.byConfidence.low}</div>
                <p className="text-sm text-muted-foreground">Low Confidence</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="candidates">
        <TabsList className="mb-4">
          <TabsTrigger value="candidates" data-testid="duplicate-candidates-tab">
            Potential Duplicates
          </TabsTrigger>
          <TabsTrigger value="groups" data-testid="duplicate-groups-tab">
            Duplicate Groups
          </TabsTrigger>
        </TabsList>

        <TabsContent value="candidates">
      {/* Filters */}
      <div className="mb-4 flex items-center gap-4">
        <Select
          value={confidenceFilter}
          onValueChange={(v) => { setConfidenceFilter(v as DuplicateConfidence | 'all'); }}
        >
          <SelectTrigger className="w-48" data-testid="confidence-filter">
            <SelectValue placeholder="Filter by confidence" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Confidence Levels</SelectItem>
            <SelectItem value="high">High Confidence</SelectItem>
            <SelectItem value="medium">Medium Confidence</SelectItem>
            <SelectItem value="low">Low Confidence</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Duplicates List */}
      {isLoading ? (
        <div className="py-8 text-center text-muted-foreground">Loading duplicates...</div>
      ) : (duplicatesData?.data ?? []).length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Copy className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
            <p className="text-lg font-medium">No duplicates found</p>
            <p className="text-muted-foreground">
              {confidenceFilter === 'all'
                ? 'All locations appear to be unique across partner accounts.'
                : `No ${confidenceFilter} confidence duplicates found.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {(duplicatesData?.data ?? []).map((duplicate) => (
            <DuplicateCard
              key={duplicate.id}
              duplicate={duplicate}
              onDismiss={() => { setConfirmDismiss(duplicate); }}
              isDismissing={dismissingId === duplicate.id}
            />
          ))}
        </div>
      )}
        </TabsContent>

        <TabsContent value="groups">
          <DuplicateGroupsTab />
        </TabsContent>
      </Tabs>

      {/* Confirm Dismiss Dialog */}
      <AlertDialog open={!!confirmDismiss} onOpenChange={() => { setConfirmDismiss(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dismiss this duplicate?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark these two locations as not being duplicates. They won't appear in the
              duplicate list again unless the detection criteria changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDismiss && handleDismiss(confirmDismiss)}
              data-testid="confirm-dismiss-btn"
            >
              Yes, dismiss
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
